import EventEmitter2 from 'eventemitter2'
import { Country, CountryNumber, type CountryType } from '../const/country.const'
import type { SnsValueType } from '../const/sns-type.const'
import { ApiUrls } from '../struct/api.struct'
import { ApiError } from '../struct/response.struct'
import { SpoonCountry } from '../struct/spoon.struct'
import type { User } from '../struct/user.struct'
import { ApiClient } from './api.client'
import { GatewayClient } from './gw.client'
import { HttpClient } from './http.client'
import { LiveClient } from './live.client'
import { StickerClient } from './sticker.client'

export class Spoon extends EventEmitter2 {
  public urls!: ApiUrls
  private httpClient!: HttpClient
  private apiClient!: ApiClient
  private liveClient!: LiveClient
  private gatewayClient!: GatewayClient
  private stickerClient!: StickerClient
  public token!: string
  public refreshToken!: string
  public deviceUniqueId!: string
  public logonUser!: User
  public appVersion = '10.10.2'

  constructor(public country: CountryType = Country.KOREA) {
    super()
  }

  get api(): ApiClient {
    return this.apiClient
  }

  get live(): LiveClient {
    return this.liveClient
  }

  get gw(): GatewayClient {
    return this.gatewayClient
  }

  get http(): HttpClient {
    return this.httpClient
  }

  get sticker(): StickerClient {
    return this.stickerClient
  }

  async initUrlsInfo() {
    this.urls = await this.httpClient.request<ApiUrls>(
      `/config/api/${this.country}.json`,
      {
        method: 'GET',
        params: {
          ts: Date.now()
        }
      },
      ApiUrls
    )
    if (!this.urls.authUrl) {
      this.urls.authUrl = `https://${this.country}-auth.spooncast.net`
    }
  }

  async getClientCountry() {
    const response = await this.httpClient.request<SpoonCountry>(
      '/country',
      {
        method: 'GET'
      },
      SpoonCountry
    )
    this.country = response.code.toLowerCase() as CountryType
  }

  async init() {
    this.httpClient = new HttpClient('https://www.spooncast.net/')
    this.deviceUniqueId = this.httpClient.userAgent.toLowerCase()
    await this.getClientCountry()
    await this.initUrlsInfo()
    this.apiClient = new ApiClient(this)
    this.liveClient = new LiveClient(this)
    this.gatewayClient = new GatewayClient(this)
    this.stickerClient = new StickerClient(this)
    await this.stickerClient.initSticker()
  }

  async initToken(sns_id: string | number, password: string, sns_type: SnsValueType) {
    const code = CountryNumber[this.country.toUpperCase() as keyof typeof CountryNumber]
    const res = await this.httpClient.request<{ data: { jwt: string; refreshToken: string } }>(
      `${this.urls.authUrl}/tokens/`,
      {
        method: 'POST',
        body: {
          auth_data: {
            act_type: sns_type,
            password,
            msisdn: Number(code + sns_id)
          },
          device_unique_id: this.deviceUniqueId
        }
      }
    )
    return await this.setToken(res.data.jwt, res.data.refreshToken)
  }

  async tokenRefresh() {
    const res = await this.httpClient.request<{ data: { jwt: string; refreshToken: string } }>(
      `${this.urls.authUrl}/tokens/`,
      {
        method: 'PUT',
        body: {
          user_id: this.logonUser.id,
          refresh_token: this.refreshToken,
          device_unique_id: this.deviceUniqueId
        }
      }
    )
    console.log('tokenRefresh Res', res)
    if (!res?.data?.jwt) {
      this.emit('error.token')
      return false
    }
    return await this.setToken(res.data.jwt)
  }

  async setToken(token: string, refreshToken?: string, assignUserId?: number) {
    this.token = token
    if (refreshToken) {
      this.refreshToken = refreshToken
    }
    this.api.instance.httpClient.appendBaseConfig({
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    this.gw.instance.httpClient.appendBaseConfig({
      headers: {
        Authorization: `Bearer ${token}`
      }
    })

    const payload = token.split('.')[1]
    const decodedPayload = JSON.parse(Buffer.from(payload, 'base64').toString())
    this.deviceUniqueId = decodedPayload.did

    if (assignUserId) {
      this.logonUser = {
        id: assignUserId
      } as User
    } else {
      const userId = decodedPayload.sub
      const res = await this.api.user.getUserInfo(userId)
      if (res instanceof ApiError) {
        throw res
      }
      this.logonUser = res.results[0]
    }
    return true
  }
}
